/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */

import {ApiError, Checkout} from "@connox/typescript-library";
import {ICartData} from "@connox/typescript-library/types/cart/CartData";
import PayPalCheckout, {PayPalTokenizePayload} from "braintree-web/paypal-checkout";
import React from "react";
import {getShopId} from "../getShopId";
import {IPaymentModule, IPurchaseButtonProps} from "./IPaymentModule";
import {PaymentController, paypalLocales} from "./PaymentController";

export default class PaypalModule implements IPaymentModule {
  private readonly checkout: Checkout;
  private readonly paypalLocale: string;
  private instance?: PayPalCheckout;
  private tokenizePayload?: PayPalTokenizePayload;
  private handleError?: (error: ApiError) => void;

  constructor(checkout: Checkout) {
    this.checkout = checkout;
    this.paypalLocale = paypalLocales[getShopId()];
    this.PurchaseButton = this.PurchaseButton.bind(this) as (props: IPurchaseButtonProps) => React.ReactElement;
    this.onEnterSummary = this.onEnterSummary.bind(this) as () => Promise<void>;
  }

  public async onSubmitPayment(): Promise<boolean> {
    const {client} = await PaymentController.initBraintreeClient();
    this.instance = (await PayPalCheckout.create({client})) as Promise<PayPalCheckout>;

    return Promise.resolve(true);
  }

  public async onEnterSummary(): Promise<void> {
    await PaymentController.initPayment();
    const paymentObject = this.buildPaymentDataObject();

    if (!paymentObject) {
      return;
    }
    if (!this.instance) {
      await this.onSubmitPayment();
    }

    const onAuthorize = async (data, action) => {
      try {
        this.tokenizePayload = (await this.instance.tokenizePayment(data)) as Promise<PayPalTokenizePayload>;
        await PaymentController.authorizePayment(this.tokenizePayload.nonce);
        await this.checkout.purchase();
      } catch (e) {
        // TODO error code 2074
        // if this
        action.restart();
        // then that
        // else log to sentry and set custo isIntoSelectPaymiAgaini
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    const payment = () => this.instance.createPayment(paymentObject);

    void import("paypal-checkout").then((paypal) => {
      paypal.Button.render(
        {
          commit: true,
          env: __DEVELOPMENT__ ? "sandbox" : "production",
          locale: this.paypalLocale,
          onAuthorize,
          onError: this.handleError,
          payment,
          style: {
            branding: true,
            height: 40,
            label: "buynow",
            shape: "rect",
            size: "responsive",
            tagline: true,
          },
        },
        "#PurchaseNow",
      );
    });
  }

  public PurchaseButton(props: IPurchaseButtonProps): React.ReactElement {
    this.handleError = props.handleError;

    return React.createElement("div", {id: "PurchaseNow"}, null);
  }

  public async onPaymentSelect(): Promise<void> {
    return Promise.resolve();
  }

  public isDataComplete(): boolean {
    return true;
  }

  private buildPaymentDataObject = (): IPayPalPayment | null => {
    const {cartData, deliveryAddress} = this.checkout;

    if (!deliveryAddress) {
      return null;
    }

    const {city, countryCode, firstName, lastName, street, zip} = deliveryAddress;
    const streetnumber = deliveryAddress.number;

    return {
      amount: cartData.value.payable.amount,
      currency: cartData.currencyCode,
      displayName: "connox - be unique",
      flow: "checkout",
      intent: "authorize",
      lineItems: getLineItems(cartData),
      locale: this.paypalLocale,
      shippingAddressEditable: false,
      shippingAddressOverride: {
        city,
        countryCode,
        line1: `${street} ${streetnumber}`,
        postalCode: zip,
        recipientName: `${firstName} ${lastName}`,
      },
    };
  };
}

export function getLineItems(cartData: ICartData): ILineItem[] {
  const itemPositions = cartData.positions.map<ILineItem>((position) => {
    const {item, price, quantity, text} = position;
    const numberPriceAmount = Number(price.amount);
    const tempObject = {
      kind: quantity < 0 !== numberPriceAmount < 0 ? "credit" : "debit",
      name: text,
      quantity: Math.abs(quantity).toString(),
      unitAmount: Math.abs(numberPriceAmount).toString(),
    };

    if (position.type === "Item" && item) {
      return {...tempObject, productCode: item.itemId.toString(), url: item.url};
    }

    return tempObject;
  });

  const rebatePositions = cartData.rebates.map<ILineItem>((rebate) => {
    return {
      kind: "credit",
      name: rebate.code,
      quantity: "1",
      unitAmount: rebate.amount.amount,
    };
  });

  return [...itemPositions, ...rebatePositions];
}

interface IPayPalPayment {
  amount: string;
  currency: string;
  displayName: string;
  flow: string;
  intent: string;
  lineItems: ILineItem[];
  locale: string;
  shippingAddressEditable: boolean;
  shippingAddressOverride: {
    city: string;
    countryCode: string;
    line1: string;
    postalCode: string;
    recipientName: string;
  };
}

// https://braintree.github.io/braintree-web/current/PayPalCheckout.html#~lineItem
interface ILineItem {
  kind: string;
  name: string;
  productCode?: string;
  quantity: string;
  unitAmount: string;
  url?: string;
}
